import { apiService } from '@/shared/services/apiServices';
import { AxiosError } from 'axios';
import { QMS_CATEGORY_ERROR_MESSAGES } from '../errors/errorMessages';
import type {
  CreateQmsCategoryPayload,
  QmsCategory,
} from '../types/qmsCategoryTypes';
import qmsCategoryEndpoints from './qmsCategoryEndpoints';

export interface CreateQmsCategoryInput {
  name: string;
  description: string;
  tags: string[];
}

export interface CreateQmsCategoryResult {
  success: boolean;
  data?: QmsCategory;
  error?: string;
  fieldErrors?: Record<string, string>;
}

interface ApiErrorData {
  message?: string;
  error?: string;
  statusCode?: number;
  code?: string;
  details?: Array<{ path?: string; message?: string; code?: string }>;
}

const getMessageFromPayload = (errorData: unknown): string | null => {
  if (!errorData) {
    return null;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData !== 'object') {
    return null;
  }
  const obj = errorData as ApiErrorData;
  return obj.message || obj.error || null;
};

const getFieldErrorsFromPayload = (
  errorData: unknown
): Record<string, string> | undefined => {
  if (!errorData || typeof errorData !== 'object') {
    return undefined;
  }
  const obj = errorData as ApiErrorData;
  const details = obj.details;
  if (!Array.isArray(details) || details.length === 0) {
    return undefined;
  }
  const fieldErrors: Record<string, string> = {};
  for (const d of details) {
    if (d?.path && d?.message) {
      fieldErrors[d.path] = d.message;
    }
  }
  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
};

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 401 || status === 403) {
    return QMS_CATEGORY_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 409) {
    return QMS_CATEGORY_ERROR_MESSAGES.NAME_ALREADY_EXISTS;
  }
  if (status === 500) {
    return QMS_CATEGORY_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (status === 400) {
    const fieldErrors = getFieldErrorsFromPayload(errorData);
    const firstDetailMessage =
      fieldErrors && Object.keys(fieldErrors).length > 0
        ? Object.values(fieldErrors)[0]
        : null;
    return (
      firstDetailMessage ||
      getMessageFromPayload(errorData) ||
      QMS_CATEGORY_ERROR_MESSAGES.VALIDATION_ERROR
    );
  }
  return (
    getMessageFromPayload(errorData) ??
    QMS_CATEGORY_ERROR_MESSAGES.DEFAULT_ERROR
  );
};

export const createQmsCategory = async (
  input: CreateQmsCategoryInput
): Promise<CreateQmsCategoryResult> => {
  try {
    const payload: CreateQmsCategoryPayload = {
      name: input.name.trim(),
      description: input.description.trim(),
      tags: input.tags,
    };

    const result = await apiService.postAPI(
      qmsCategoryEndpoints.CATEGORIES,
      payload,
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      return { success: true, data: result.data as QmsCategory };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: QMS_CATEGORY_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    const errorMessage = extractErrorMessage(result.data, result.status);
    let fieldErrors = getFieldErrorsFromPayload(result.data);

    if (result.status === 409) {
      fieldErrors = {
        ...(fieldErrors ?? {}),
        name: QMS_CATEGORY_ERROR_MESSAGES.NAME_ALREADY_EXISTS,
      };
    }

    return {
      success: false,
      error: errorMessage,
      ...(fieldErrors && { fieldErrors }),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      if (isNetworkError) {
        return {
          success: false,
          error: QMS_CATEGORY_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      const errorMessage = extractErrorMessage(errorData, status);
      let fieldErrors = getFieldErrorsFromPayload(errorData);

      if (status === 409) {
        fieldErrors = {
          ...(fieldErrors ?? {}),
          name: QMS_CATEGORY_ERROR_MESSAGES.NAME_ALREADY_EXISTS,
        };
      }

      return {
        success: false,
        error: errorMessage,
        ...(fieldErrors && { fieldErrors }),
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : QMS_CATEGORY_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
